import React, { useState } from "react";
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { Column, Row, Text, Icon } from "@amzn/meridian";
import Textarea from "@amzn/meridian/textarea";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { InspectionResult } from "src/services/inspectionService";
import { useAppDispatch } from "src/store/store";
import { overrideInspectionResult } from "src/store/inspectionResultsSlice";
import ThemedTag from "./ThemedTag";
import "./overrideCountPage.scss";

/**
 * Override page for auto-calculated count questions (today: hazardous container
 * count). Unlike the compliance override, the inspector adjusts a numeric count
 * with a stepper and provides a justification instead of a Yes/No answer plus
 * evidence photos.
 *
 * On submit the entered count is stored as `confirmed_result` (stringified) and
 * `is_compliant` is always sent as "true" — a manually corrected count is treated
 * as compliant. The backend preserves the original computed value in
 * `calculatedValue`, which the review sheet shows on its "Original result" tab.
 */
const OverrideCountPage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const state = location.state as {
        result: InspectionResult;
        questionText: string;
        /** Upper bound: hazardous containers can't exceed the total inspected. */
        maxCount?: number;
    } | null;

    // The stepper starts from confirmedResult: for a fresh result that equals the
    // system-computed count, and for an already-overridden one it's the inspector's
    // corrected count (so re-opening the page preserves their prior value).
    const confirmedCount = Number(state?.result.confirmedResult ?? 0);
    const initialCount = Number.isFinite(confirmedCount) ? confirmedCount : 0;

    const [count, setCount] = useState<number>(initialCount);
    const [comment, setComment] = useState<string>("");
    const [commentTouched, setCommentTouched] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    if (!state) {
        return <Navigate to={`/configurationDrivenInspections/${inspectionId}/review`} replace />;
    }

    const { result, questionText, maxCount } = state;

    // Only enforce a max when a positive total was provided.
    const hasMax = typeof maxCount === "number" && maxCount > 0;
    const atMax = hasMax && count >= maxCount;

    const trimmedComment = comment.trim();
    const hasReason = trimmedComment !== "";
    const canSubmit = hasReason;

    const decrement = () => setCount((c) => Math.max(0, c - 1));
    const increment = () => setCount((c) => (hasMax ? Math.min(maxCount, c + 1) : c + 1));

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        setSubmitError(null);

        const formData = new FormData();
        formData.append("result_id", result.resultId);
        formData.append("ai_result", result.aiResult || "");
        formData.append("confirmed_result", String(count));
        // A manually corrected count is always treated as compliant.
        formData.append("is_compliant", "true");
        formData.append("override_reason", trimmedComment);

        try {
            await dispatch(overrideInspectionResult({
                inspection_id: inspectionId!,
                question_id: result.questionId,
                formData,
            })).unwrap();

            navigate(-1);
        } catch (error) {
            // .unwrap() on a rejectWithValue(message) throws the message as a string.
            console.error("Failed to submit count override:", error);
            const message =
                typeof error === "string"
                    ? error
                    : error instanceof Error
                        ? error.message
                        : "Failed to submit override. Please try again.";
            setSubmitError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="override-count-page pwa-scrollable">
            <Column spacingInset="500" spacing="500">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="override-count-page__back"
                        onClick={() => navigate(-1)}
                        role="button"
                    >
                        <Icon tokens={arrowLeftTokens} color="success" />
                    </div>
                    <Text type="b300" color="primary">
                        Override Compliance
                    </Text>
                </Row>

                <p className="override-count-page__question-text">{questionText}</p>

                <Row
                    alignmentHorizontal="justify"
                    alignmentVertical="center"
                    spacingInset="400"
                    className="override-count-page__auto-box"
                >
                    <Text type="b300" color="secondary">Auto-computed count</Text>
                    <Row spacing="300" alignmentVertical="center">
                        <Text type="h300" color="primary">{result.calculatedValue}</Text>
                        <ThemedTag text="AUTO" type="neutral" />
                    </Row>
                </Row>

                <Column spacing="200">
                    <Text type="h100" color="primary">
                        Corrected container count <span className="override-count-page__required">*</span>
                    </Text>
                    <Row spacing="none" alignmentVertical="center" className="override-count-page__stepper">
                        <button
                            type="button"
                            className="override-count-page__stepper-btn"
                            onClick={decrement}
                            disabled={count <= 0}
                            aria-label="Decrease count"
                        >
                            −
                        </button>
                        <div className="override-count-page__stepper-value" aria-live="polite">
                            {count}
                        </div>
                        <button
                            type="button"
                            className="override-count-page__stepper-btn"
                            onClick={increment}
                            disabled={atMax}
                            aria-label="Increase count"
                        >
                            +
                        </button>
                    </Row>
                    {hasMax && (
                        <Text type="b100" color="secondary">
                            Cannot exceed {maxCount} container(s) inspected.
                        </Text>
                    )}
                </Column>

                <Column spacing="100">
                    <Text type="h100" color="primary">Reason (Required)</Text>
                    <Textarea
                        value={comment}
                        onChange={(value: string) => {
                            setComment(value);
                            if (!commentTouched) {
                                setCommentTouched(true);
                            }
                            if (submitError) {
                                setSubmitError(null);
                            }
                        }}
                        onBlur={() => setCommentTouched(true)}
                        placeholder="Explain why the auto-computed count is being changed (e.g. two containers stacked behind the pallet were not captured in the inspection photos)."
                        rows={4}
                        error={commentTouched && !hasReason}
                        errorMessage={
                            commentTouched && !hasReason
                                ? "A reason is required to override a result."
                                : undefined
                        }
                    />
                </Column>

                {submitError && (
                    <Text type="b100" color="error">{submitError}</Text>
                )}
            </Column>

            <button
                className={`override-count-page__submit-btn ${canSubmit ? "override-count-page__submit-btn--enabled" : ""}`}
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
            >
                {isSubmitting ? "SUBMITTING..." : "SUBMIT OVERRIDE"}
            </button>
        </div>
    );
};

export default OverrideCountPage;
